import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, FolderOpen, FileText, Download, Trash2, Upload } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Templates = () => {
  const { user } = useContext(AuthContext);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    file: null
  });

  const categories = [
    { value: "meeting_minutes", label: "Toplantı Tutanağı" },
    { value: "nonconformity", label: "Uygunsuzluk Bildirim Formu" },
    { value: "audit_info", label: "Denetim Bilgi Formu" },
    { value: "checklist", label: "Kontrol Listesi" },
    { value: "certificate", label: "Sertifika" },
    { value: "other", label: "Diğer" }
  ];

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`, { withCredentials: true });
      setTemplates(response.data);
    } catch (error) {
      toast.error("Şablonlar yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.file) {
      toast.error("Lütfen bir dosya seçin");
      return;
    }

    setUploading(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append("name", formData.name);
      formDataObj.append("category", formData.category);
      formDataObj.append("description", formData.description || "");
      formDataObj.append("file", formData.file);

      await axios.post(`${API}/templates`, formDataObj, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success("Şablon yüklendi");
      setDialogOpen(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Yükleme başarısız");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm("Bu şablonu silmek istediğinizden emin misiniz?")) return;
    try {
      await axios.delete(`${API}/templates/${templateId}`, { withCredentials: true });
      toast.success("Şablon silindi");
      fetchTemplates();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  const downloadTemplate = async (templateId, fileName) => {
    try {
      const response = await axios.get(`${API}/templates/${templateId}/download`, {
        withCredentials: true,
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Şablon indirildi");
    } catch (error) {
      toast.error("İndirme başarısız");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      category: "",
      file: null
    });
  };

  const getCategoryLabel = (value) => {
    return categories.find(c => c.value === value)?.label || value;
  };

  const getFileTypeColor = (type) => {
    const colors = {
      pdf: "bg-red-100 text-red-800",
      docx: "bg-blue-100 text-blue-800",
      xlsx: "bg-green-100 text-green-800"
    };
    return colors[type] || "bg-gray-100 text-gray-800";
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="templates-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#111827]">Şablon Yönetimi</h1>
          <p className="text-[#4B5563]">Denetim dokümanı şablonlarını yönetin</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#1A4D2E] hover:bg-[#1A4D2E]/90" data-testid="add-template-btn">
              <Plus className="w-4 h-4 mr-2" />
              Şablon Yükle
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Yeni Şablon Yükle</DialogTitle>
              <DialogDescription>PDF, Word veya Excel dosyası yükleyin</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="name">Şablon Adı *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="template-name-input"
                  placeholder="Örn: Toplantı Tutanağı"
                />
              </div>
              <div>
                <Label>Kategori *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                  required
                >
                  <SelectTrigger data-testid="template-category-select">
                    <SelectValue placeholder="Kategori seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="description">Açıklama</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  data-testid="template-description-input"
                  placeholder="Şablon hakkında kısa açıklama..."
                  rows={2}
                />
              </div>
              <div>
                <Label htmlFor="file">Dosya *</Label>
                <div className="mt-2">
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-[#E8F3EE] rounded-lg cursor-pointer hover:bg-[#F9FAFB] transition-colors">
                    {formData.file ? (
                      <div className="text-center">
                        <FileText className="w-8 h-8 mx-auto text-[#1A4D2E] mb-2" />
                        <p className="text-sm text-[#111827]">{formData.file.name}</p>
                        <p className="text-xs text-[#9CA3AF] mt-1">Değiştirmek için tıklayın</p>
                      </div>
                    ) : (
                      <div className="text-center">
                        <Upload className="w-8 h-8 mx-auto text-[#9CA3AF] mb-2" />
                        <p className="text-sm text-[#4B5563]">Dosya seçmek için tıklayın</p>
                        <p className="text-xs text-[#9CA3AF] mt-1">PDF, DOCX, XLSX</p>
                      </div>
                    )}
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.docx,.xlsx"
                      onChange={(e) => setFormData({ ...formData, file: e.target.files[0] })}
                      data-testid="template-file-input"
                    />
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  İptal
                </Button>
                <Button 
                  type="submit" 
                  className="bg-[#1A4D2E] hover:bg-[#1A4D2E]/90" 
                  disabled={uploading}
                  data-testid="save-template-btn"
                >
                  {uploading ? "Yükleniyor..." : "Yükle"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-[#1A4D2E] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : templates.length === 0 ? (
        <Card className="border-[#E8F3EE]">
          <CardContent className="py-12 text-center">
            <FolderOpen className="w-12 h-12 mx-auto mb-4 text-[#9CA3AF]" />
            <p className="text-[#4B5563]">Henüz şablon yüklenmemiş</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <Card key={template.template_id} className="border-[#E8F3EE] hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-[#1A4D2E]/10 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-[#1A4D2E]" />
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${getFileTypeColor(template.file_type)}`}>
                    {template.file_type.toUpperCase()}
                  </span>
                </div>
                <h3 className="font-semibold text-[#111827] mb-1">{template.name}</h3>
                <p className="text-sm text-[#9CA3AF] mb-3">{getCategoryLabel(template.category)}</p>
                {template.description && (
                  <p className="text-sm text-[#4B5563] mb-4 line-clamp-2">{template.description}</p>
                )}
                <p className="text-xs text-[#9CA3AF] mb-4">
                  {format(new Date(template.created_at), "dd MMM yyyy", { locale: tr })}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => downloadTemplate(template.template_id, template.file_name)}
                    data-testid={`download-template-${template.template_id}`}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    İndir
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(template.template_id)}
                    data-testid={`delete-template-${template.template_id}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Templates;
